'use client';

/**
 * DataTableHeader Component
 *
 * Sortable table header with theme-aware styling and neon accents.
 */

import { styled, useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import type { HeaderGroup } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type React from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { transitionPresets } from '../../theme/transitions';
import { fontSizes, letterSpacings } from '../../theme/typography';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface DataTableHeaderProps<T> {
  /** Header groups from react-table */
  headerGroups: HeaderGroup<T>[];
  /** Enable sorting */
  sortable?: boolean;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Enable row selection column */
  hasSelectionColumn?: boolean;
  /** Enable column resizing */
  columnResizing?: boolean;
}

// ============================================================================
// Icon Components
// ============================================================================

const ArrowUpIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
  </svg>
);

const ArrowDownIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
  </svg>
);

const UnfoldMoreIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M12 5.83L15.17 9l1.41-1.41L12 3 7.41 7.59 8.83 9 12 5.83zm0 12.34L8.83 15l-1.41 1.41L12 21l4.59-4.59L15.17 15 12 18.17z" />
  </svg>
);

// ============================================================================
// Styled Components
// ============================================================================

interface StyledHeaderCellProps {
  $isDark: boolean;
  $sortable: boolean;
  $neonAccent?: NeonAccent;
  $align?: 'left' | 'center' | 'right';
}

const StyledHeaderCell = styled(
  TableCell,
  transientOptions,
)<StyledHeaderCellProps>(({ theme, $isDark, $sortable, $neonAccent, $align = 'left' }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    fontWeight: 600,
    fontSize: fontSizes['2xs'],
    textTransform: 'uppercase',
    letterSpacing: letterSpacings.wider,
    color: $isDark ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.55)',
    borderBottom: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
    backgroundColor: $isDark ? 'rgba(30, 30, 34, 0.85)' : 'rgba(245, 245, 247, 0.88)',
    backdropFilter: 'blur(20px) saturate(180%)',
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
    padding: theme.spacing(0.875, 1.5),
    textAlign: $align,
    transition: transitionPresets.colors,
    whiteSpace: 'nowrap',

    ...($sortable && {
      cursor: 'pointer',
      userSelect: 'none',

      '&:hover': {
        backgroundColor: $isDark ? 'rgba(40, 40, 46, 0.9)' : 'rgba(238, 238, 242, 0.92)',
        color: neonStyles ? neonStyles.base : $isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
      },
    }),
  };
});

interface SortIndicatorProps {
  $active: boolean;
  $neonAccent?: NeonAccent;
  $isDark: boolean;
}

const SortIndicator = styled(
  'span',
  transientOptions,
)<SortIndicatorProps>(({ $active, $neonAccent, $isDark }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 4,
    opacity: $active ? 1 : 0.3,
    transition: transitionPresets.allFast,
    color: $active && neonStyles ? neonStyles.base : 'inherit',

    '& svg': {
      fontSize: '1rem',
    },
  };
});

const HeaderContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

interface StyledResizeHandleProps {
  $isDark: boolean;
  $isResizing: boolean;
  $neonAccent?: NeonAccent;
}

const StyledResizeHandle = styled(
  'div',
  transientOptions,
)<StyledResizeHandleProps>(({ $isDark, $isResizing, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const accentColor = neonStyles?.base ?? ($isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)');

  return {
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
    width: 8,
    cursor: 'col-resize',
    userSelect: 'none',
    touchAction: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    // The visible line
    '&::after': {
      content: '""',
      width: 2,
      height: '50%',
      borderRadius: 1,
      backgroundColor: $isResizing ? accentColor : 'transparent',
      transition: 'background-color 150ms ease',
    },

    '&:hover::after': {
      backgroundColor: accentColor,
    },

    // Glow effect when actively resizing
    ...($isResizing &&
      neonStyles && {
        '&::after': {
          content: '""',
          width: 2,
          height: '50%',
          borderRadius: 1,
          backgroundColor: neonStyles.base,
          boxShadow: `0 0 6px ${neonStyles.base}`,
        },
      }),
  };
});

// ============================================================================
// Component
// ============================================================================

export function DataTableHeader<T>({
  headerGroups,
  sortable = false,
  neonAccent,
  columnResizing = false,
}: DataTableHeaderProps<T>): React.ReactElement {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const renderSortIndicator = (isSorted: false | 'asc' | 'desc', canSort: boolean) => {
    if (!sortable || !canSort) return null;

    return (
      <SortIndicator $active={!!isSorted} $neonAccent={neonAccent} $isDark={isDark}>
        {isSorted === 'asc' ? <ArrowUpIcon /> : isSorted === 'desc' ? <ArrowDownIcon /> : <UnfoldMoreIcon />}
      </SortIndicator>
    );
  };

  return (
    <TableHead>
      {headerGroups.map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const canSort = header.column.getCanSort();
            const isSorted = header.column.getIsSorted();
            const columnDef = header.column.columnDef;
            const align = (columnDef.meta as { align?: 'left' | 'center' | 'right' } | undefined)?.align;

            // Only pin a width when resizing is active OR the column has an
            // explicit size on its columnDef. Without this, tanstack's default
            // 150px size would anchor every unsized column even with resizing
            // off, undermining the natural HTML table layout that lets columns
            // distribute proportionally across the container.
            const hasExplicitSize = header.column.columnDef.size !== undefined;
            const widthStyle = columnResizing || hasExplicitSize ? { width: header.getSize() } : undefined;

            return (
              <StyledHeaderCell
                key={header.id}
                $isDark={isDark}
                $sortable={sortable && canSort}
                $neonAccent={neonAccent}
                $align={align}
                onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                style={widthStyle}
              >
                {header.isPlaceholder ? null : (
                  <HeaderContent>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {renderSortIndicator(isSorted, canSort)}
                  </HeaderContent>
                )}
                {columnResizing && header.column.getCanResize() && (
                  <StyledResizeHandle
                    $isDark={isDark}
                    $isResizing={header.column.getIsResizing()}
                    $neonAccent={neonAccent}
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={() => header.column.resetSize()}
                  />
                )}
              </StyledHeaderCell>
            );
          })}
        </TableRow>
      ))}
    </TableHead>
  );
}

export default DataTableHeader;
